import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button, IconButton } from "@mui/material";
import {
  Loader2,
  Menu,
  PackageCheck,
  RefreshCw,
  Save,
  Search,
  X,
} from "lucide-react";
import DoctorHeader from "../doctor_dashboard/DoctorHeader";
import DoctorSidebar, { DoctorSidebarDrawer } from "../doctor_dashboard/DoctorSidebar";
import SimpleSnackbar from "../Snackbar";

const statusOptions = [
  "pending",
  "paid_product",
  "failed",
  "processing",
  "dispatch",
  "delivered",
];

const statusLabels = {
  pending: "Pending product payment",
  paid_product: "Paid",
  failed: "Failed",
  processing: "Processing",
  dispatch: "Dispatched",
  delivered: "Delivered",
};

const badgeStyles = {
  pending: "bg-amber-50 text-amber-700",
  paid_product: "bg-emerald-50 text-emerald-700",
  failed: "bg-red-50 text-red-700",
  processing: "bg-slate-100 text-slate-700",
  dispatch: "bg-emerald-50 text-emerald-700",
  delivered: "bg-emerald-50 text-emerald-700",
};

function formatNaira(value) {
  return `₦${Number(value || 0).toLocaleString("en-NG")}`;
}

function getHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  };
}

function toEditForm(order = {}) {
  return {
    status: order.status || "pending",
    riderName: order.rider?.name || "",
    riderPhone: order.rider?.phoneNumber || "",
    riderVehicle: order.rider?.vehicle || "",
    riderTrackingCode: order.rider?.trackingCode || "",
    note: order.adminNote || "",
  };
}

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold ${
        badgeStyles[status] || "bg-slate-100 text-slate-700"
      }`}
    >
      {statusLabels[status] || status}
    </span>
  );
}

function EditOrderModal({ order, form, setForm, saving, onClose, onSave }) {
  if (!order) return null;

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end bg-slate-950/50 px-3 pb-3 sm:items-center sm:justify-center sm:p-6">
      <div className="relative max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5 shadow-xl sm:p-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50"
          aria-label="Close"
        >
          <X size={17} />
        </button>

        <div className="pr-10">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-600">
            Home test order
          </p>
          <h2 className="mt-2 text-xl font-semibold tracking-[-0.03em] text-slate-950">
            {order.title}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {order.client?.name} · {order.client?.email}
          </p>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <label className="space-y-1.5">
            <span className="text-xs font-semibold text-slate-500">Status</span>
            <select
              value={form.status}
              onChange={(event) => update("status", event.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-semibold text-slate-800 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
            >
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {statusLabels[status]}
                </option>
              ))}
            </select>
          </label>

          <label className="space-y-1.5">
            <span className="text-xs font-semibold text-slate-500">Rider name</span>
            <input
              value={form.riderName}
              onChange={(event) => update("riderName", event.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-semibold text-slate-800 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
              placeholder="Rider name"
            />
          </label>

          <label className="space-y-1.5">
            <span className="text-xs font-semibold text-slate-500">Rider phone</span>
            <input
              value={form.riderPhone}
              onChange={(event) => update("riderPhone", event.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-semibold text-slate-800 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
              placeholder="Rider phone"
            />
          </label>

          <label className="space-y-1.5">
            <span className="text-xs font-semibold text-slate-500">Vehicle</span>
            <input
              value={form.riderVehicle}
              onChange={(event) => update("riderVehicle", event.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-semibold text-slate-800 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
              placeholder="Bike, car, plate number"
            />
          </label>

          <label className="space-y-1.5">
            <span className="text-xs font-semibold text-slate-500">
              Tracking code
            </span>
            <input
              value={form.riderTrackingCode}
              onChange={(event) => update("riderTrackingCode", event.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-semibold text-slate-800 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
              placeholder="Optional"
            />
          </label>

          <label className="space-y-1.5 sm:col-span-2">
            <span className="text-xs font-semibold text-slate-500">Note</span>
            <textarea
              value={form.note}
              onChange={(event) => update("note", event.target.value)}
              className="min-h-[96px] w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-semibold text-slate-800 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
              placeholder="Optional customer-facing or admin note"
            />
          </label>
        </div>

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50"
          >
            Close
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
            Save order
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminHomeTests() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editForm, setEditForm] = useState(toEditForm());
  const [saving, setSaving] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState("success");

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/home-tests/orders`,
        { headers: getHeaders() }
      );

      setOrders(response.data?.orders || []);
      setProducts(response.data?.products || []);
    } catch (error) {
      console.error(error);
      setSnackMessage(error.response?.data?.message || "Could not load home tests");
      setSnackColor("error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const filteredOrders = useMemo(() => {
    const value = search.trim().toLowerCase();

    return orders.filter((order) => {
      const matchesStatus =
        statusFilter === "all" || order.status === statusFilter;
      const matchesSearch =
        !value ||
        [
          order.title,
          order.orderId,
          order.reference,
          order.client?.name,
          order.client?.email,
          order.client?.phoneNumber,
          order.statusLabel,
        ]
          .join(" ")
          .toLowerCase()
          .includes(value);

      return matchesStatus && matchesSearch;
    });
  }, [orders, search, statusFilter]);

  const stats = useMemo(() => {
    return orders.reduce(
      (acc, order) => {
        acc.total += 1;
        if (order.status === "paid_product") acc.paid += 1;
        if (order.status === "dispatch") acc.dispatched += 1;
        if (order.status === "delivered") acc.delivered += 1;
        acc.productRevenue += Number(order.amountPaid || 0);
        return acc;
      },
      {
        total: 0,
        paid: 0,
        dispatched: 0,
        delivered: 0,
        productRevenue: 0,
      }
    );
  }, [orders]);

  const openEdit = (order) => {
    setSelectedOrder(order);
    setEditForm(toEditForm(order));
  };

  const saveOrder = async () => {
    if (!selectedOrder?.orderId) return;

    try {
      setSaving(true);
      const response = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/admin/home-tests/orders/${selectedOrder.orderId}`,
        editForm,
        { headers: getHeaders() }
      );

      const nextOrder = response.data?.order;
      if (nextOrder) {
        setOrders((current) =>
          current.map((order) =>
            order.orderId === nextOrder.orderId ? nextOrder : order
          )
        );
      }

      setSnackMessage(response.data?.message || "Order updated");
      setSnackColor("success");
      setSelectedOrder(null);
    } catch (error) {
      console.error(error);
      setSnackMessage(error.response?.data?.message || "Could not update order");
      setSnackColor("error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-screen flex-col bg-slate-50 text-slate-950">
      <DoctorHeader />

      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 md:hidden">
        <div>
          <p className="text-xs text-slate-400">Admin</p>
          <h1 className="text-base font-semibold text-slate-950">Home Tests</h1>
        </div>

        <IconButton onClick={() => setDrawerOpen(true)}>
          <Menu />
        </IconButton>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="hidden lg:flex">
          <DoctorSidebar activeTab="Home Tests" setActiveTab={() => {}} />
        </div>

        <DoctorSidebarDrawer
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
          onClose={() => setDrawerOpen(false)}
          activeTab="Home Tests"
          setActiveTab={() => setDrawerOpen(false)}
        />

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-600">
                  Admin
                </p>
                <h1 className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-slate-950 sm:text-3xl">
                  Home Tests
                </h1>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                  Manage home testing kit orders, rider details and customer
                  status updates. Delivery is included in the kit price.
                </p>
              </div>

              <Button
                onClick={fetchOrders}
                disabled={loading}
                startIcon={
                  loading ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : (
                    <RefreshCw size={16} />
                  )
                }
                sx={{
                  borderRadius: "12px",
                  background: "#0f172a",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 700,
                  px: 2,
                  "&:hover": { background: "#020617" },
                }}
              >
                Refresh
              </Button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-semibold text-slate-500">Orders</p>
                <p className="mt-2 text-2xl font-semibold text-slate-950">
                  {stats.total}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-semibold text-slate-500">
                  Paid orders
                </p>
                <p className="mt-2 text-2xl font-semibold text-slate-950">
                  {stats.paid}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-semibold text-slate-500">Dispatched</p>
                <p className="mt-2 text-2xl font-semibold text-slate-950">
                  {stats.dispatched}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-semibold text-slate-500">Revenue</p>
                <p className="mt-2 text-2xl font-semibold text-slate-950">
                  {formatNaira(stats.productRevenue)}
                </p>
              </div>
            </div>

            <section className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-2">
                  <PackageCheck className="text-emerald-600" size={19} />
                  <h2 className="text-sm font-semibold text-slate-950">
                    Product catalogue
                  </h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  {products.map((product) => (
                    <span
                      key={product.id}
                      className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600"
                    >
                      {product.name} · {formatNaira(product.price)}
                    </span>
                  ))}
                </div>
              </div>
            </section>

            <section className="mt-5 rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-col gap-3 border-b border-slate-200 p-4 lg:flex-row lg:items-center lg:justify-between">
                <label className="flex w-full items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 lg:max-w-sm">
                  <Search size={16} className="text-slate-400" />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-slate-800 outline-none placeholder:text-slate-400"
                    placeholder="Search orders"
                  />
                </label>

                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                >
                  <option value="all">All statuses</option>
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {statusLabels[status]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-100 text-left">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500">
                        Order
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500">
                        Customer
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500">
                        Payment
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500">
                        Status
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500">
                        Address
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {loading ? (
                      <tr>
                        <td
                          colSpan="6"
                          className="px-4 py-12 text-center text-sm font-semibold text-slate-500"
                        >
                          <Loader2 className="mx-auto mb-2 animate-spin" size={20} />
                          Loading orders
                        </td>
                      </tr>
                    ) : filteredOrders.length ? (
                      filteredOrders.map((order) => (
                        <tr key={order.orderId} className="align-top">
                          <td className="px-4 py-4">
                            <p className="text-sm font-semibold text-slate-950">
                              {order.title}
                            </p>
                            <p className="mt-1 text-xs text-slate-400">
                              {order.orderId}
                            </p>
                          </td>
                          <td className="px-4 py-4">
                            <p className="text-sm font-semibold text-slate-800">
                              {order.client?.name || "Patient"}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              {order.client?.email}
                            </p>
                            <p className="mt-1 text-xs text-slate-400">
                              {order.client?.phoneNumber}
                            </p>
                          </td>
                          <td className="px-4 py-4 text-sm font-semibold text-slate-700">
                            <p>Total: {formatNaira(order.amountPaid || order.price)}</p>
                            <p className="mt-1 text-xs text-slate-500">
                              Delivery included
                            </p>
                          </td>
                          <td className="px-4 py-4">
                            <StatusBadge status={order.status} />
                          </td>
                          <td className="max-w-xs px-4 py-4 text-xs leading-5 text-slate-500">
                            {order.deliveryAddress?.fullAddress || "No address"}
                          </td>
                          <td className="px-4 py-4 text-right">
                            <div className="flex justify-end gap-2">
                              <a
                                href={order.trackingUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex h-9 items-center rounded-xl border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                              >
                                Track
                              </a>
                              <button
                                type="button"
                                onClick={() => openEdit(order)}
                                className="inline-flex h-9 items-center rounded-xl bg-slate-900 px-3 text-xs font-semibold text-white hover:bg-slate-800"
                              >
                                Edit
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="6"
                          className="px-4 py-12 text-center text-sm font-semibold text-slate-500"
                        >
                          No home test orders found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </main>
      </div>


      <EditOrderModal
        order={selectedOrder}
        form={editForm}
        setForm={setEditForm}
        saving={saving}
        onClose={() => setSelectedOrder(null)}
        onSave={saveOrder}
      />

      <SimpleSnackbar message={snackMessage} color={snackColor} />
    </div>
  );
}
